// spec-13: the platform-wide activation funnel — the aggregate view that
// COMPLEMENTS the per-entity FunnelStrip ("where is THIS org?") by answering "where
// does the WHOLE population drop off?". The Nivo funnel gives the shape; the strip
// below it spells out each stage's count and the step-to-step conversion % (the
// actual leak), since Nivo's funnel doesn't label conversion natively. Fed by
// GET /api/stats/activation-funnel (getActivationFunnel).
import { ResponsiveFunnel, type FunnelDatum } from "@nivo/funnel";
import { insightsTheme, useVibrantPalette } from "./insights/theme";

export interface FunnelStage {
  key: string;
  label: string;
  count: number;
}

function pct(n: number, d: number): number {
  return d > 0 ? Math.round((n / d) * 100) : 0;
}

export function ActivationFunnel({ stages }: { stages: FunnelStage[] }) {
  const vibrant = useVibrantPalette();
  stages = stages ?? [];
  const denom = stages[0]?.count ?? 0;

  if (!stages.length || denom === 0) {
    return (
      <p className="text-sm text-muted" data-testid="funnel-empty">
        No activation data yet.
      </p>
    );
  }

  const data: FunnelDatum[] = stages.map((s) => ({
    id: s.key,
    label: s.label,
    value: s.count,
  }));

  return (
    <div data-testid="activation-funnel">
      <div className="relative h-56" role="img" aria-label="Platform activation funnel">
        <ResponsiveFunnel
          data={data}
          margin={{ top: 16, right: 24, bottom: 16, left: 24 }}
          direction="horizontal"
          colors={vibrant}
          theme={insightsTheme}
          borderWidth={12}
          labelColor={{ from: "color", modifiers: [["darker", 3]] }}
          beforeSeparatorLength={20}
          afterSeparatorLength={20}
          currentPartSizeExtension={8}
          animate
        />
      </div>
      {/* The leak, in numbers: count + conversion from the previous stage and from
          the top of the funnel. Counts shown alongside % so soft-launch volumes
          don't mislead. */}
      <ol className="mt-3 grid gap-1 text-sm">
        {stages.map((s, i) => {
          const prev = i === 0 ? null : stages[i - 1].count;
          const stepPct = prev == null ? 100 : pct(s.count, prev);
          const overallPct = pct(s.count, denom);
          return (
            <li
              key={s.key}
              data-testid={`funnel-stage-${s.key}`}
              className="flex items-center justify-between border-b border-edge-subtle py-1 last:border-0"
            >
              <span className="text-secondary">{s.label}</span>
              <span className="flex items-center gap-3">
                <span className="font-medium text-primary" data-testid={`funnel-count-${s.key}`}>
                  {s.count}
                </span>
                <span className="text-muted" data-testid={`funnel-pct-${s.key}`}>
                  {i === 0 ? `${overallPct}%` : `${stepPct}% from prev · ${overallPct}% of signups`}
                </span>
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
